import { Container, Graphics } from '../../../vendor/pixi.min.mjs';

/**
 * "Black-hole" confetti swirl that twists AROUND the adventure end
 * building once it becomes enterable. Where {@link SnakePortalConfetti}
 * funnels particles straight into a wall-mounted gate, this orbits a
 * free-standing building: particles spawn on an elliptical ring hugging
 * the house footprint and spiral INWARD on an accelerating curve while
 * sweeping through several turns, fading out as they get "sucked" past
 * the building edge — so the whole structure reads as a swirling vortex
 * the player is meant to dive into.
 *
 * Geometry is pushed every frame via {@link setCenter} (the building
 * never moves mid-stage, but the world container can scroll, so we keep
 * re-anchoring). Pass `null` to stop emission; in-flight particles
 * finish their spiral so the effect tapers cleanly when the house is
 * cleared (board transition / win).
 *
 * Pooled: each finished particle's Graphics returns to a free pool and
 * is re-used, so a long dwell on the final screen never keeps allocating
 * fresh Graphics.
 */

// Festive multi-colour confetti (vs the portal's cold blue stream) so
// the "you made it, enter here!" moment reads as celebratory.
const PALETTE = [
    0xfca5a5, // red
    0xfcd34d, // amber
    0x86efac, // green
    0x5eead4, // teal
    0x93c5fd, // blue
    0xc4b5fd, // violet
    0xf9a8d4, // pink
    0xffffff, // white sparkle
];

const MAX_PARTICLES = 130;
// Particles spawned per frame while a centre is set. Fractional —
// accumulated across frames so the spawn rate is delta-correct.
const EMIT_PER_FRAME = 2.6;

const LIFETIME_FRAMES_MIN = 44;
const LIFETIME_FRAMES_MAX = 74;

const SIZE_MIN = 3;
const SIZE_MAX = 8.5;

// How many full orbits a particle sweeps over its lifetime — the swirl.
const TURNS_MIN = 1.3;
const TURNS_MAX = 2.7;

// Ring radius as a multiple of the building half-extent: particles
// START on an outer ring (clearly around the building) and spiral DOWN
// to the inner radius, where they vanish "into" the structure.
const RADIUS_START_MULT = 1.75;
const RADIUS_END_MULT = 0.42;

export class SnakeHouseVortex {
    container = new Container();

    /**
     * @type {Array<{
     *   gfx: Graphics, t: number, tStep: number,
     *   cx: number, cy: number,
     *   rxStart: number, ryStart: number, rxEnd: number, ryEnd: number,
     *   angle0: number, turns: number, dir: number,
     *   size: number,
     * }>}
     */
    _active = [];

    /** @type {Graphics[]} */
    _pool = [];

    _emitAccum = 0;

    /** @type {null | { x:number, y:number, halfW:number, halfH:number }} */
    _center = null;

    /** Swirl direction (+1 / -1), randomised once per construction so a
     *  given playthrough keeps a consistent rotation sense. */
    _dir = Math.random() < 0.5 ? -1 : 1;

    /**
     * Set (or clear) the building the vortex orbits.
     * @param {number|null} x  World-space centre X, or `null` to stop emitting.
     * @param {number} [y]      World-space centre Y.
     * @param {number} [halfW]  Half the building width  (px).
     * @param {number} [halfH]  Half the building height (px).
     */
    setCenter(x, y = 0, halfW = 0, halfH = 0) {
        if (x == null) { this._center = null; return; }
        this._center = {
            x, y,
            halfW: Math.max(8, halfW),
            halfH: Math.max(8, halfH),
        };
    }

    /**
     * Advance emission + per-particle motion by `delta` frames
     * (PixiJS ticker value, ≈1 at 60 fps).
     */
    update(delta = 1) {
        if (this._center) {
            this._emitAccum += EMIT_PER_FRAME * delta;
            let safety = 24;
            while (this._emitAccum >= 1 && safety-- > 0) {
                this._emitAccum -= 1;
                if (this._active.length < MAX_PARTICLES) this._spawnOne();
            }
        }

        for (let i = this._active.length - 1; i >= 0; i--) {
            const p = this._active[i];
            p.t += p.tStep * delta;
            if (p.t >= 1) { this._recycle(i); continue; }

            // Accelerating inward pull (t²) so a particle lingers on the
            // outer orbit then snaps toward the building — the black-hole
            // "event horizon" feel.
            const rt = p.t * p.t;
            const rx = p.rxStart + (p.rxEnd - p.rxStart) * rt;
            const ry = p.ryStart + (p.ryEnd - p.ryStart) * rt;
            const angle = p.angle0 + p.dir * p.turns * Math.PI * 2 * p.t;

            p.gfx.x = p.cx + Math.cos(angle) * rx;
            p.gfx.y = p.cy + Math.sin(angle) * ry;
            // Spin each chip in step with its orbit + shrink as it's
            // drawn in so it visibly "compresses" into the vortex.
            p.gfx.rotation = angle * 1.5;
            p.gfx.scale.set(p.size * (1 - 0.55 * p.t));

            // Fade in over the first 12%, ride full, then fade out over
            // the final 30% so the chip dissolves into the building.
            let alpha;
            if (p.t < 0.12) alpha = p.t / 0.12;
            else if (p.t > 0.70) alpha = (1 - p.t) / 0.30;
            else alpha = 1;
            p.gfx.alpha = Math.max(0, alpha);
        }
    }

    /** Return every active particle to the pool immediately. */
    clear() {
        while (this._active.length > 0) {
            this._recycle(this._active.length - 1);
        }
        this._emitAccum = 0;
    }

    destroy() {
        this.clear();
        for (const g of this._pool) {
            if (!g.destroyed) g.destroy();
        }
        this._pool.length = 0;
        if (!this.container.destroyed) {
            this.container.destroy({ children: true });
        }
    }

    // ── internals ─────────────────────────────────────────

    _spawnOne() {
        if (!this._center) return;
        const g = this._acquire();

        const { x: cx, y: cy, halfW, halfH } = this._center;
        const lifeFrames = LIFETIME_FRAMES_MIN
            + Math.random() * (LIFETIME_FRAMES_MAX - LIFETIME_FRAMES_MIN);
        const size = SIZE_MIN + Math.random() * (SIZE_MAX - SIZE_MIN);
        const color = PALETTE[Math.floor(Math.random() * PALETTE.length)];

        // Unit square scaled in `update` — keeps the geometry rebuild
        // out of the hot path.
        g.clear();
        g.rect(-0.5, -0.5, 1, 1);
        g.fill(color);
        g.scale.set(size);
        g.alpha = 0;

        // A little per-particle jitter on the start ring so the swirl
        // reads as a cloud band rather than a single hard circle.
        const jitter = 0.85 + Math.random() * 0.4;

        this._active.push({
            gfx: g,
            t: 0,
            tStep: 1 / lifeFrames,
            cx, cy,
            rxStart: halfW * RADIUS_START_MULT * jitter,
            ryStart: halfH * RADIUS_START_MULT * jitter,
            rxEnd: halfW * RADIUS_END_MULT,
            ryEnd: halfH * RADIUS_END_MULT,
            angle0: Math.random() * Math.PI * 2,
            turns: TURNS_MIN + Math.random() * (TURNS_MAX - TURNS_MIN),
            dir: this._dir,
            size,
        });
        this.container.addChild(g);
    }

    _recycle(i) {
        const p = this._active[i];
        if (p.gfx.parent) p.gfx.parent.removeChild(p.gfx);
        p.gfx.alpha = 0;
        this._pool.push(p.gfx);
        this._active.splice(i, 1);
    }

    _acquire() {
        return this._pool.pop() || new Graphics();
    }
}
